import type { ContentCardProps } from '../../types';
import { Card, Icon, Stack } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { I18n } from '~/common/explicit-exports';
import { getIconColor } from '~/utils';
import { CardDescription, CardTitle } from '../CardText';
import { CardFooter } from '../CardFooter';
import { buildCardMetadata } from '../../utils/cardMetadataHelpers';

export interface GuideCardProps extends ContentCardProps {
  progressTasks?: number;
  totalTasks?: number;
  duration?: number | null;
  skills?: string[];
  onStart?: () => void;
  onPreview?: () => void;
}

const { DEFAULT_COVER_IMAGES } = window.APP_CONSTANTS;

export const GuideCard = ({
  name,
  imageSrc,
  description,
  progressTasks,
  totalTasks,
  duration,
  assignmentStatus,
  onStart,
  onPreview,
  tags = [],
  skills = [],
  overflowMenuItems = {},
  hideContentCTA = false,
  actions,
}: GuideCardProps) => {
  const theme = useTheme();

  const progressPercent = totalTasks ? Math.round((progressTasks / totalTasks) * 100) : 0;
  const START = I18n.t('js.learner.libraries.start_guide');
  const PROGRESS = I18n.t('js.learner.libraries.guide_progress', {
    progress: progressPercent,
  });

  const metadata = buildCardMetadata(duration, skills, tags, theme.colors.on[600]);

  const iconColor = getIconColor(theme.colors.decorative.blue[500]);
  return (
    <Card onPress={onStart}>
      {imageSrc && (
        <Card.Hero
          src={imageSrc}
          errorImage={DEFAULT_COVER_IMAGES.GUIDE}
          icon={<Icon color={iconColor} name="guide:regular" size="lg" />}
        />
      )}
      <Card.Container data-testid="library-card">
        <Card.Content>
          <Card.Body>
            <Stack size={4}>
              {!imageSrc && <Card.Icon iconColor={iconColor} iconName="guide:regular" />}
              <Stack size={2}>
                <CardTitle text={name} />
                {metadata.length ? <Card.Metadata metadata={metadata} /> : null}
                <CardDescription text={description} color={theme.colors.on[500]} />
              </Stack>
            </Stack>
          </Card.Body>
        </Card.Content>
      </Card.Container>
      <CardFooter
        assignmentStatus={assignmentStatus}
        inProgressMessage={PROGRESS}
        startContentMessage={START}
        onStart={onStart}
        onPreview={onPreview}
        overflowMenuItems={overflowMenuItems}
        hideContentCTA={hideContentCTA}
        actions={actions}
      />
    </Card>
  );
};
